// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Pläne-Katalog: Herkunfts-Gruppen als Listen (Skill §13.7 — die Überschrift ersetzt
// Chip und Kartenrahmen). Eine Zeile öffnet die Detail-Ansicht (/plaene/:id) —
// dort wird gewählt, abgewandelt, gelöscht (Thors Feedback Runde 7).
import { useEffect, useState, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { getPlans, getPlanDays, getPlanDayItems, activePlanId, onSynced, sofort } from "@/lib/data"
import { createPlan, planBadge } from "@/lib/plans"
import { nachKategorie, startVorschlaege } from "@/lib/planKategorien"
import { toast } from "@/lib/toast"
import type { Plan, PlanDay, PlanDayItem } from "@/lib/types"
import Wichtel from "@/components/celebration/creatures/Wichtel"
import "@/styles/plans.css"

const OFFLINE_MSG = "Dafür brauchst du Internet – bitte später erneut versuchen"

type Badge = ReturnType<typeof planBadge>
// Eigene zuerst; die 33 Vorlagen stehen weiter unten nach Kategorie sortiert
const GRUPPEN: Array<[Badge, string]> = [
  ["dein Plan", "Deine Pläne"],
  ["von einem Mitglied", "Von Mitgliedern"],
]

export default function PlansPage() {
  const navigate = useNavigate()
  // Sofort-Speicher: beim Tab-Wechsel steht die Seite schon (Runde 16)
  const [plans, setPlans] = useState<Plan[]>(() => sofort.plans() ?? [])
  const [days, setDays] = useState<PlanDay[]>(() => sofort.planDays() ?? [])
  const [items, setItems] = useState<PlanDayItem[]>(() => sofort.planDayItems() ?? [])
  const [showForm, setShowForm] = useState(false)
  const [newName, setNewName] = useState("")
  const [busy, setBusy] = useState(false)
  const [offeneKat, setOffeneKat] = useState<string | null>(null)

  async function load() {
    const [ps, ds, its] = await Promise.all([getPlans(), getPlanDays(), getPlanDayItems()])
    setPlans(ps)
    setDays(ds)
    setItems(its)
  }
  useEffect(() => {
    void load()
    return onSynced(() => { void load() })
  }, [])

  const sorted = [...plans].sort((a, b) => a.name.localeCompare(b.name, "de"))

  const vorschlaege = startVorschlaege(plans)
  const kategorien = nachKategorie(sorted.filter((p) => planBadge(p) === "Vorlage"))

  const planZeile = (p: Plan) => (
    <div className="plan-row" key={p.id}>
      <button type="button" className="plan-row-head" onClick={() => navigate(`/plaene/${p.id}`)}>
        <span className="plan-row-name">{p.name}</span>
        {p.id === activePlanId() && <span className="plan-aktiv">✓ aktiv</span>}
        <span className="plan-row-pfeil" aria-hidden="true">›</span>
        <span className="plan-row-meta">{metaLine(p)}</span>
      </button>
    </div>
  )

  function metaLine(p: Plan): string {
    const dayIds = new Set(days.filter((d) => d.plan === p.id).map((d) => d.id))
    const n = items.filter((i) => dayIds.has(i.plan_day)).length
    return `${dayIds.size} ${dayIds.size === 1 ? "Tag" : "Tage"} · ${n} ${n === 1 ? "Übung" : "Übungen"}`
  }

  async function onCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    setBusy(true)
    try {
      const plan = await createPlan(name)
      navigate(`/plaene/${plan.id}/bearbeiten`)
    } catch {
      toast(OFFLINE_MSG)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {showForm ? (
        <form className="card newplan" onSubmit={(e) => void onCreate(e)}>
          <label htmlFor="newplan-name">Name des neuen Plans</label>
          <input
            id="newplan-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
          <div className="newplan-actions">
            <button type="submit" className="btn-secondary" disabled={busy || !newName.trim()}>Anlegen</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </form>
      ) : sorted.length > 0 ? (
        /* Flache Zone statt Voll-Tanne: das Tanne-Budget dieser Seite bleibt frei (Skill §5) */
        <button className="btn-secondary newplan-open" onClick={() => setShowForm(true)}>+ Neuer Plan</button>
      ) : null}

      {sorted.length === 0 ? (
        <div className="empty">
          <div className="critter"><Wichtel /></div>
          <p><b>Kein Plan? Bau dir einen.</b></p>
          <button className="btn-secondary" onClick={() => setShowForm(true)}>Plan anlegen</button>
        </div>
      ) : (
        <>
          {/* Ohne gewählten Plan zuerst drei verschiedene Richtungen statt
              einer 33er-Liste (Thors Wunsch Runde 16) */}
          {!activePlanId() && vorschlaege.length > 0 && (
            <section>
              <h2>Für den Anfang</h2>
              <div className="plan-list">
                {vorschlaege.map((v) => (
                  <div className="plan-row" key={v.plan.id}>
                    <button type="button" className="plan-row-head" onClick={() => navigate(`/plaene/${v.plan.id}`)}>
                      <span className="plan-row-name">{v.plan.name}</span>
                      <span className="plan-row-pfeil" aria-hidden="true">›</span>
                      <span className="plan-row-meta plan-warum">{v.warum}</span>
                    </button>
                  </div>
                ))}
              </div>
              <p className="hint">Nichts dabei? Unten stehen alle Vorlagen nach Sportart sortiert.</p>
            </section>
          )}

          {GRUPPEN.map(([badge, titel]) => {
            const gruppe = sorted.filter((p) => planBadge(p) === badge)
            if (!gruppe.length) return null
            return (
              <section key={badge}>
                <h2>{titel}</h2>
                <div className="plan-list">
                  {gruppe.map((p) => planZeile(p))}
                </div>
              </section>
            )
          })}

          {/* 33 Vorlagen als eine Liste sind ein Katalog. Nach Sportart geklappt
              sieht man auf einen Blick, was es überhaupt gibt. */}
          {kategorien.length > 0 && (
            <section>
              <h2>Vorlagen nach Sportart</h2>
              <div className="plan-list">
                {kategorien.map(([kat, gruppe]) => {
                  const id = kat?.id ?? "weitere"
                  const offen = offeneKat === id
                  return (
                    <div className={offen ? "kat offen" : "kat"} key={id}>
                      <button type="button" className="kat-head" aria-expanded={offen}
                        onClick={() => setOffeneKat(offen ? null : id)}>
                        <span className="kat-name">{kat?.name ?? "Weitere"}</span>
                        <span className="kat-pfeil" aria-hidden="true">›</span>
                        <span className="kat-meta">
                          {kat?.hinweis ?? "Ohne feste Sportart"} · {gruppe.length} Pläne
                        </span>
                      </button>
                      {offen && <div className="kat-body">{gruppe.map((p) => planZeile(p))}</div>}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </>
      )}
    </>
  )
}
